import {
  BoxProps,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  SimpleGrid,
} from '@chakra-ui/react';
import { KeyMapper, LabelMapper, ValueMapper } from '../mapperTypes';
import { InputHTMLAttributes, PropsWithChildren } from 'react';
import { Field, FieldArray, FieldProps } from 'formik';
import useTranslate from '~/common/utils/translate';
import { Text } from './Text';
import _ from 'lodash';

type CheckboxSelectProps<T, O extends string | number> = {
  fields: T[];
  labels: LabelMapper<T>;
  values: ValueMapper<T, O>;
  keys: KeyMapper<T>;
  label?: string;
  name: string;
  row?: boolean;
  boxProps?: BoxProps;
  params?: string[];
  errorParams?: string[];
  columns?: number;
  isNumber?: boolean;
  orderBy?: ((d: T) => unknown) | ((d: T) => unknown)[] | string | false;
} & InputHTMLAttributes<HTMLInputElement> & { 'aria-invalid'?: true };

export function CheckboxSelect<T, O extends string | number>({
  fields,
  labels,
  values,
  keys,
  name,
  label,
  row = false,
  boxProps,
  params,
  columns = 1,
  orderBy,
  isNumber = false,
  ...rest
}: PropsWithChildren<CheckboxSelectProps<T, O>>) {
  const translate = useTranslate();

  return (
    <FormControl {...boxProps}>
      <Flex flexDirection={row ? 'row' : 'column'}>
        {!label || <FormLabel>{translate(label, params)}</FormLabel>}
        <FieldArray
          name={name}
          render={() => {
            return (
              <SimpleGrid columns={columns}>
                {fields && fields.length > 0 ? (
                  (orderBy === false
                    ? fields
                    : _.orderBy(fields, orderBy ?? labels)
                  ).map((f) => {
                    const value = `${values(f)}`;
                    return (
                      <div key={keys(f)}>
                        <Field
                          name={`${name}`}
                          type="checkbox"
                          value={value}
                          {...rest}
                        >
                          {({ field }: FieldProps) => {
                            return (
                              <Checkbox
                                isChecked={field.checked}
                                name={field.name}
                                onChange={(e) =>
                                  isNumber
                                    ? field.onChange({
                                        ...e,
                                        target: {
                                          ...e.target,
                                          value: e.target.valueAsNumber,
                                        },
                                      })
                                    : field.onChange(e)
                                }
                                onBlur={field.onBlur}
                                multiple={field.multiple}
                                value={field.value as string | number}
                              >
                                {labels(f)}
                              </Checkbox>
                            );
                          }}
                        </Field>
                      </div>
                    );
                  })
                ) : (
                  <Text>checkboxSelect.nofields.label</Text>
                )}
              </SimpleGrid>
            );
          }}
        />
      </Flex>
    </FormControl>
  );
}
